import React, { useMemo } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { BasicInformationStepProps } from './types';
import { GENDER_OPTIONS, SEX_AT_BIRTH_OPTIONS } from './constants';
import countryOptions from '@/data/countries.json';

interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({ label, required = false, error, children }) => (
  <div className="space-y-1.5">
    <div className="relative">
      <span className="pointer-events-none absolute -top-2 left-3 z-10 bg-background px-1 align-middle font-normal text-[0.75rem] leading-[12px] tracking-[0.15px] text-muted-foreground [font-family:Inter,sans-serif]">
        {label}
        {required ? ' *' : ''}
      </span>
      {children}
    </div>
    {error ? <p className="text-xs text-destructive">{error}</p> : null}
  </div>
);

const inputClassName =
  'h-14 rounded-md border-border bg-background text-base text-foreground placeholder:text-muted-foreground focus-visible:ring-cs-primary/30';
const selectTriggerClassName =
  `${inputClassName} focus:!border-ring focus:!ring-0 focus:!ring-offset-0 focus-visible:!border-ring focus-visible:!ring-0 focus-visible:!ring-offset-0`;

const BasicInformationStep: React.FC<BasicInformationStepProps> = ({
  formData,
  step1Form,
  handleInputChange,
  handlePhoneChange,
  phoneCountry,
  initialValues,
}) => {
  const maxDate = useMemo(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }, []);

  const firstNonEmpty = (...values: Array<string | undefined>) => {
    for (const value of values) {
      if (typeof value === 'string' && value.trim().length > 0) return value;
    }
    return '';
  };

  const phoneCountryCode = firstNonEmpty(
    step1Form.watch('phone_country_code'),
    formData.phone_country_code,
    initialValues?.phone_country_code
  );

  const phoneNumber = firstNonEmpty(
    step1Form.watch('phone'),
    formData.phone
  );

  const showSexAtBirthField =
    !!formData.gender && formData.gender !== 'male' && formData.gender !== 'female';

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">Basic Details</h3>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <FormField
            label="First name"
            required
            error={step1Form.formState.errors.firstName?.message as string | undefined}
          >
            <Input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="First Name"
              className={cn(
                inputClassName,
                step1Form.formState.errors.firstName && 'border-destructive focus-visible:ring-destructive/20'
              )}
            />
          </FormField>

          <FormField
            label="Last name"
            required
            error={step1Form.formState.errors.lastName?.message as string | undefined}
          >
            <Input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Last Name"
              className={cn(
                inputClassName,
                step1Form.formState.errors.lastName && 'border-destructive focus-visible:ring-destructive/20'
              )}
            />
          </FormField>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">Contact Information</h3>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <FormField
            label="Email"
            required
            error={step1Form.formState.errors.email?.message as string | undefined}
          >
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter your client's email"
              className={cn(
                inputClassName,
                step1Form.formState.errors.email && 'border-destructive focus-visible:ring-destructive/20'
              )}
            />
          </FormField>

          <FormField
            label="Country"
            required
            error={step1Form.formState.errors.location?.message as string | undefined}
          >
            <Select value={formData.location} onValueChange={(value) => handleInputChange('location', value)}>
              <SelectTrigger
                className={cn(
                  selectTriggerClassName,
                  step1Form.formState.errors.location && 'border-destructive focus:ring-destructive/20'
                )}
              >
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent className="z-[10060] max-h-80">
                {countryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <FormField label="City / County">
            <Input
              type="text"
              value={formData.cityOrCounty || ''}
              onChange={(e) => handleInputChange('cityOrCounty', e.target.value)}
              placeholder="e.g. Hyderabad"
              className={inputClassName}
            />
          </FormField>

          <FormField
            label="Phone number"
            required
            error={step1Form.formState.errors.phone?.message as string | undefined}
          >
            <PhoneInput
              key={`phone-${phoneCountry}-${phoneCountryCode}`}
              country={phoneCountry}
              value={phoneCountryCode.replace('+', '') + phoneNumber}
              onChange={handlePhoneChange}
              countryCodeEditable={false}
              inputProps={{
                name: 'phone',
                required: true,
                autoFocus: false,
              }}
              containerClass={cn(
                '!relative !w-full !h-14 !rounded-md !border !bg-background !flex !items-center',
                '!border-border focus-within:!ring-2 focus-within:!ring-cs-primary/30',
                step1Form.formState.errors.phone &&
                  '!border-destructive focus-within:!ring-destructive/20'
              )}
              inputClass={cn(
                '!h-14 !w-full !box-border !border-0 !bg-transparent !py-2 !pl-16 !pr-3 !text-base !leading-normal !text-foreground !shadow-none !outline-none !ring-0 placeholder:!text-muted-foreground'
              )}
              buttonClass="!h-14 !rounded-l-md !border-0 !bg-transparent !px-3 !z-[320]"
              dropdownClass="!border-border !bg-background !z-[321]"
              dropdownStyle={{ zIndex: 9999, backgroundColor: 'hsl(var(--background))' }}
            />
          </FormField>

          <FormField label="Zip code">
            <Input
              type="text"
              value={formData.zipCode || ''}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
              placeholder="Zip code"
              className={inputClassName}
            />
          </FormField>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">Other Information</h3>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <FormField
            label="Select DOB"
            required
            error={step1Form.formState.errors.dateOfBirth?.message as string | undefined}
          >
            <Input
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              max={maxDate}
              className={cn(
                inputClassName,
                step1Form.formState.errors.dateOfBirth && 'border-destructive focus-visible:ring-destructive/20'
              )}
            />
          </FormField>

          <FormField
            label="Select Gender"
            required
            error={step1Form.formState.errors.gender?.message as string | undefined}
          >
            <Select
              value={formData.gender}
              onValueChange={(value) => {
                handleInputChange('gender', value);
                if (value === 'male' || value === 'female') {
                  handleInputChange('sexAtBirth', value);
                } else {
                  handleInputChange('sexAtBirth', '');
                }
              }}
            >
              <SelectTrigger
                className={cn(
                  selectTriggerClassName,
                  step1Form.formState.errors.gender && 'border-destructive focus:ring-destructive/20'
                )}
              >
                <SelectValue placeholder="Select Gender" />
              </SelectTrigger>
              <SelectContent className="z-[210]">
                {GENDER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          {showSexAtBirthField && (
            <FormField
              label="Sex at Birth"
              required
              error={step1Form.formState.errors.sexAtBirth?.message as string | undefined}
            >
              <Select
                value={formData.sexAtBirth || ''}
                onValueChange={(value) => handleInputChange('sexAtBirth', value)}
              >
                <SelectTrigger
                  className={cn(
                    selectTriggerClassName,
                    step1Form.formState.errors.sexAtBirth && 'border-destructive focus:ring-destructive/20'
                  )}
                >
                  <SelectValue placeholder="Select sex at birth" />
                </SelectTrigger>
                <SelectContent className="z-[210]">
                  {SEX_AT_BIRTH_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          )}
        </div>
      </section>
    </div>
  );
};

export default BasicInformationStep;
